import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Folder, ModelUtils } from '../models';
import { useSelectedTemplatingItem } from './useSelectedTemplatingItem';
import { useCallback } from 'react';

const FOLDER_STATE_STORAGE_KEY = 'ai-templates-folder-states';

type UseFolderStateArgs = {
  folder: Folder;
};

export const useFolderState = ({ folder }: UseFolderStateArgs) => {
  const queryClient = useQueryClient();

  const {
    selectedTemplatingItemQuery: {
      data: selectedTemplatingItem,
      isLoading: isLoadingSelectedTemplatingItem,
    },
    selectedTemplatingItemId,
  } = useSelectedTemplatingItem();

  const { data: { isExpanded } = { isExpanded: false } } = useQuery({
    queryKey: ['ai-templates', 'folder-state', folder.path],
    queryFn: () => {
      const stored = getFolderStatesFromStorage();
      const shouldExpand = selectedTemplatingItem
        ? ModelUtils.isChildOf(folder, selectedTemplatingItem)
        : false;

      return { isExpanded: stored[folder.path] || shouldExpand };
    },
    enabled: selectedTemplatingItemId ? !isLoadingSelectedTemplatingItem : true,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const setFolderExpandedState = useCallback(
    (isExpanded: boolean) => {
      saveFolderStateToStorage(folder.path, isExpanded);
      queryClient.setQueryData(['ai-templates', 'folder-state', folder.path], {
        isExpanded,
      });
    },
    [folder.path, queryClient],
  );

  const toggleFolderExpanded = useCallback(() => {
    setFolderExpandedState(!isExpanded);
  }, [setFolderExpandedState, isExpanded]);

  const expandFolder = useCallback(() => {
    setFolderExpandedState(true);
  }, [setFolderExpandedState]);

  return {
    isExpanded,
    toggleFolderExpanded,
    expandFolder,
  };
};

function getFolderStatesFromStorage(): Record<string, boolean> {
  const stored = localStorage.getItem(FOLDER_STATE_STORAGE_KEY);
  return stored ? JSON.parse(stored) : {};
}

function saveFolderStateToStorage(folderPath: string, isExpanded: boolean) {
  const currentStates = getFolderStatesFromStorage();
  const newStates = { ...currentStates, [folderPath]: isExpanded };
  localStorage.setItem(FOLDER_STATE_STORAGE_KEY, JSON.stringify(newStates));
}
